import React from 'react';
import { FieldError } from 'react-hook-form';

import { InputLabel, SxProps, Theme } from '@mui/material';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import { Asterisk } from '@/components/Asterisk';

type AutoCompleteSingleSelectProps<T> = {
  id?: string;
  options: T[];
  value?: T;
  error?: FieldError;
  readOnly?: boolean;
  disabled?: boolean;
  inputValue?: string;
  multiple?: boolean;
  sx?: SxProps<Theme>;
  isRequired?: boolean;
  labelSx?: SxProps<Theme>;
  filterSelectedOptions?: boolean;
  label?: React.ReactNode | string | null;
  getOptionLabel?: (option: T) => string;
  onChange?: (
    event: React.SyntheticEvent,
    value: T,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>,
  ) => void;
  onInputChange?: (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => void;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState,
  ) => React.ReactNode;
  getOptionDisabled?: (option: T) => boolean;
};

export const AutoCompleteSingleSelect = <T extends { label: string }>({
  id,
  sx,
  error,
  value,
  label,
  options,
  labelSx,
  disabled,
  inputValue,
  isRequired,
  filterSelectedOptions,
  getOptionDisabled,
  getOptionLabel,
  onInputChange,
  renderOption,
  onChange,
}: AutoCompleteSingleSelectProps<T>) => (
  <>
    <InputLabel htmlFor={id} sx={labelSx}>
      {label}
      {isRequired ? <Asterisk /> : ''}
    </InputLabel>
    <Autocomplete
      id={id}
      sx={sx}
      value={value}
      autoHighlight
      options={options}
      disabled={disabled}
      inputValue={inputValue}
      getOptionLabel={getOptionLabel}
      getOptionDisabled={getOptionDisabled}
      filterSelectedOptions={filterSelectedOptions}
      renderInput={(params) => <TextField {...params} error={!!error} helperText={error?.message} />}
      onInputChange={onInputChange}
      renderOption={renderOption}
      onChange={onChange}
      ListboxProps={{
        sx: {
          width: 'fit-content',
          minWidth: '100%',
          '& .MuiAutocomplete-option': {
            whiteSpace: 'nowrap',
          },
        },
      }}
    />
  </>
);
